import React, { useState } from 'react';
import { BookOpen, Download, Video, MessageCircle, Phone, Mail, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const HelpGuide = () => {
  const [activeTab, setActiveTab] = useState('quick');

  const tabs = [
    { id: 'quick', label: 'Quick Start', icon: CheckCircle },
    { id: 'full', label: 'Panduan Lengkap', icon: BookOpen },
    { id: 'faq', label: 'FAQ', icon: MessageCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 px-6 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <BookOpen className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            Panduan Pengguna
          </h1>
          <p className="text-sm text-slate-500 mt-1 dark:text-slate-400">
            Panduan lengkap setup dan penggunaan sistem billing
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'quick' && <QuickStartContent />}
          {activeTab === 'full' && <FullGuideContent />}
          {activeTab === 'faq' && <FAQContent />}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Support */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Butuh Bantuan?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-green-600 mt-0.5 dark:text-green-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">WhatsApp</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">+62-812-3456-7890</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5 dark:text-blue-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Email</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">support@radiuslink.id</p>
                </div>
              </div>
              <div className="pt-2 border-t">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  <strong>Jam Operasional:</strong><br />
                  Senin-Jumat, 09:00-17:00 WIB
                </p>
              </div>
            </CardContent>
          </Card>

          {/* MikroTik Setup Guides */}
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                📡 Setup MikroTik
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <h4 className="font-semibold text-sm text-blue-900 mb-2">Mode Koneksi RADIUS:</h4>
                <div className="space-y-2">
                  <a 
                    href="/app/radius-only-guide" 
                    className="block p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors dark:bg-slate-800 dark:hover:bg-blue-900/40"
                  >
                    <p className="font-semibold text-sm text-blue-800">🟢 RADIUS Only</p>
                    <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">Untuk router dengan IP Publik</p>
                  </a>
                  <a 
                    href="/app/radius-guide" 
                    className="block p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors dark:bg-slate-800 dark:hover:bg-blue-900/40"
                  >
                    <p className="font-semibold text-sm text-blue-800">🔒 RADIUS + VPN</p>
                    <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">Untuk router tanpa IP Publik</p>
                  </a>
                  <a 
                    href="/app/hybrid-mode-guide" 
                    className="block p-3 bg-white border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors dark:bg-slate-800 dark:hover:bg-purple-900/40"
                  >
                    <p className="font-semibold text-sm text-purple-800">🔀 Hybrid Mode</p>
                    <p className="text-xs text-purple-600 mt-1 dark:text-purple-400">Campuran: RADIUS Only + VPN</p>
                  </a>
                </div>
              </div>
              <div className="pt-2 border-t border-blue-200">
                <a 
                  href="/app/vpn-setup-guide" 
                  className="block p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors dark:bg-slate-800 dark:hover:bg-blue-900/40"
                >
                  <p className="font-semibold text-sm text-blue-800">🔐 VPN Setup</p>
                  <p className="text-xs text-blue-600 mt-1 dark:text-blue-400">Panduan L2TP VPN lengkap</p>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Video Tutorial */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Video className="w-5 h-5 text-red-600 dark:text-red-400" />
                Video Tutorial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-3 dark:text-slate-300">
                Coming soon! Video tutorial akan segera tersedia.
              </p>
              <Button variant="outline" className="w-full" disabled>
                <Video className="w-4 h-4 mr-2" />
                Lihat Video
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Quick Start Content
const QuickStartContent = () => {
  const steps = [
    {
      number: 1,
      title: 'Isi Data Perusahaan',
      description: 'Lengkapi informasi perusahaan untuk invoice dan laporan (WAJIB)',
      time: '5 menit',
      status: 'important',
      link: '/app/company-profile',
      mandatory: true,
    },
    {
      number: 2,
      title: 'Buat Paket Layanan',
      description: 'Buat minimal 2-3 paket layanan (Basic, Standard, Premium) - WAJIB untuk pelanggan',
      time: '5 menit',
      status: 'important',
      link: '/app/service-plans',
      mandatory: true,
    },
    {
      number: 3,
      title: 'Tambah Cabang',
      description: 'Buat minimal 1 cabang - WAJIB untuk pelanggan',
      time: '3 menit',
      status: 'important',
      link: '/app/branches',
      mandatory: true,
    },
    {
      number: 4,
      title: 'Tambah Pemilik',
      description: 'Tambahkan minimal 1 pemilik/owner - WAJIB untuk pelanggan',
      time: '2 menit',
      status: 'important',
      link: '/app/owners',
      mandatory: true,
    },
    {
      number: 5,
      title: 'Setup Pengaturan',
      description: 'WhatsApp, Payment Gateway, SMTP, MikroTik (Opsional tapi recommended)',
      time: '10 menit',
      status: 'pending',
      link: '/app/settings',
      mandatory: false,
    },
    {
      number: 6,
      title: 'Tambah Pelanggan',
      description: 'Setelah langkah 1-4 selesai, baru bisa tambah pelanggan',
      time: '3 menit',
      status: 'pending',
      link: '/app/customers',
      mandatory: false,
      requires: ['company', 'service_plans', 'branches', 'owners'],
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">🚀 Setup dalam 15 Menit</CardTitle>
        <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">
          Ikuti 5 langkah ini untuk mulai menggunakan sistem billing
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {steps.map((step) => (
          <div
            key={step.number}
            className={`flex gap-4 p-4 rounded-lg border ${
              step.status === 'completed'
                ? 'bg-green-50 border-green-200'
                : step.status === 'important'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step.status === 'completed'
                  ? 'bg-green-600 text-white'
                  : step.status === 'important'
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-300 text-slate-600'
              }`}
            >
              {step.status === 'completed' ? '✓' : step.number}
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-200">{step.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">{step.description}</p>
                  <p className="text-xs text-slate-500 mt-2 dark:text-slate-400">⏱️ Estimasi: {step.time}</p>
                </div>
                {step.link && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => (window.location.href = step.link)}
                  >
                    Mulai
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
          <p className="text-sm text-blue-800">
            <strong>💡 Tips:</strong> Setelah setup selesai, Anda bisa mulai menambahkan lebih
            banyak pelanggan, setup integrasi MikroTik, dan mengaktifkan automation.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Full Guide Content
const FullGuideContent = () => {
  const sections = [
    {
      title: '1. Login Pertama Kali',
      content: [
        'Gunakan kredensial yang diberikan oleh administrator',
        'Format username: admin@<kode-tenant>.com',
        'Segera ganti password setelah login pertama',
      ],
    },
    {
      title: '2. Mengisi Data Perusahaan (WAJIB)',
      content: [
        'Menu: Pengaturan → Pengaturan Perusahaan',
        'Isi: Nama, Alamat, Telepon, Email, NPWP, Rekening Bank',
        'Data ini akan muncul di invoice dan laporan',
        'Pastikan data akurat dan profesional',
      ],
    },
    {
      title: '3. Membuat Cabang (Opsional)',
      content: [
        'Menu: Cabang → Tambah Cabang',
        'Diperlukan jika memiliki beberapa lokasi kantor',
        'Skip langkah ini jika hanya punya satu lokasi',
      ],
    },
    {
      title: '4. Membuat Paket Layanan (WAJIB)',
      content: [
        'Menu: Paket Layanan → Tambah Paket',
        'Buat minimal 2-3 paket dengan harga bervariasi',
        'Contoh: Basic (10 Mbps), Standard (20 Mbps), Premium (50 Mbps)',
        'Anda HARUS membuat paket sebelum bisa menambahkan pelanggan',
      ],
    },
    {
      title: '5. Menambahkan Pelanggan',
      content: [
        'Menu: Pelanggan → Tambah Pelanggan',
        'Isi data lengkap: Nama, Email, Telepon, Alamat',
        'Pilih paket layanan yang sudah dibuat',
        'Set tanggal aktivasi dan jatuh tempo',
      ],
    },
    {
      title: '6. Setup Integrasi MikroTik (Opsional)',
      content: [
        'Menu: Pengaturan → MikroTik',
        'Pastikan MikroTik API sudah aktif',
        'Masukkan IP, Port, Username, Password',
        'Test koneksi sebelum menyimpan',
      ],
    },
    {
      title: '7. Setup Payment Gateway (Opsional)',
      content: [
        'Daftar akun di Xendit atau Duitku',
        'Menu: Pengaturan → Payment Gateway',
        'Masukkan API Key dari provider',
        'Aktifkan metode pembayaran yang diinginkan',
      ],
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">📚 Panduan Lengkap Setup</CardTitle>
        <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">
          Panduan detail step-by-step untuk setup sistem
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {sections.map((section, idx) => (
          <div key={idx} className="border-l-4 border-blue-600 pl-4">
            <h3 className="font-semibold text-slate-800 mb-2 dark:text-slate-200">{section.title}</h3>
            <ul className="space-y-1">
              {section.content.map((item, itemIdx) => (
                <li key={itemIdx} className="text-sm text-slate-600 flex items-start gap-2 dark:text-slate-300">
                  <span className="text-blue-600 mt-0.5 dark:text-blue-400">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// FAQ Content
const FAQContent = () => {
  const faqs = [
    {
      q: 'Bagaimana cara mengganti password?',
      a: 'Masuk ke menu Pengaturan → Profile, lalu klik tombol "Ganti Password". Masukkan password lama dan password baru, kemudian klik Simpan.',
    },
    {
      q: 'Apakah bisa menghapus pelanggan?',
      a: 'Ya, bisa. Tapi pastikan pelanggan tidak memiliki invoice yang belum dibayar. Sebaiknya set status pelanggan menjadi "NON-ACTIVE" daripada menghapus.',
    },
    {
      q: 'Bagaimana cara isolir pelanggan yang telat bayar?',
      a: 'Anda bisa isolir manual dari halaman pelanggan, atau aktifkan automation auto-isolir (hubungi admin untuk aktivasi).',
    },
    {
      q: 'Invoice tidak ter-generate otomatis, kenapa?',
      a: 'Pastikan tanggal jatuh tempo pelanggan sudah diset dengan benar. Invoice otomatis ter-generate pada tanggal jatuh tempo setiap bulan.',
    },
    {
      q: 'Bagaimana cara menambahkan user admin lain?',
      a: 'Menu: Users → Tambah User. Pilih role "admin" dan tentukan permissions yang sesuai.',
    },
    {
      q: 'Apakah data saya aman?',
      a: 'Ya, semua data terenkripsi dan tersimpan aman. Kami tidak pernah membagikan data Anda ke pihak ketiga. Backup data Anda secara berkala untuk keamanan ekstra.',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">❓ Frequently Asked Questions</CardTitle>
        <p className="text-sm text-slate-600 mt-2 dark:text-slate-300">
          Jawaban untuk pertanyaan yang sering diajukan
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {faqs.map((faq, idx) => (
          <div key={idx} className="border-b border-slate-200 pb-4 last:border-0 last:pb-0 dark:border-slate-700">
            <h3 className="font-semibold text-slate-800 mb-2 dark:text-slate-200">{faq.q}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">{faq.a}</p>
          </div>
        ))}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/30">
          <p className="text-sm text-blue-800">
            <strong>Tidak menemukan jawaban?</strong><br />
            Hubungi support kami via WhatsApp atau Email (lihat sidebar kanan).
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default HelpGuide;
